import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useTenant } from '../lib/tenant';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Lock, Mail, Shield, Building2 } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_securedeck/artifacts/7gltxyqf_BSMS%20Rapid%20X.png";
const HERO_IMAGE_URL = "https://customer-assets.emergentagent.com/job_3d01a576-c163-458c-8e02-d8e86222e312/artifacts/c7bo0sl1_Rapid%20X%20Tech%20Pty%20Ltd.png";

export default function LoginPage() {
  const { login } = useAuth();
  const { tenant, error: tenantError } = useTenant();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      if (result.requiresPasswordChange) {
        navigate('/change-password');
      } else {
        navigate(
          result.user?.role === 'contractor_admin'
            ? '/contractor/dashboard'
            : '/dashboard',
          { replace: true },
        );
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  // If tenant resolution failed, show error
  if (tenantError && !tenant) {
    return (
      <div className="min-h-screen bg-[#07070a] flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <Building2 className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Organisation Not Found</h1>
          <p className="text-sm text-zinc-400 mb-4">{tenantError}</p>
          <p className="text-xs text-zinc-600">
            Check your URL or contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <Card className="w-full max-w-md bg-[#18181b]/95 backdrop-blur-sm border-[#3f3f46] shadow-2xl shadow-blue-500/10">
          <CardHeader className="space-y-1 text-center pb-6">
            <div className="flex justify-center mb-4">
              <img 
                src={LOGO_URL} 
                alt="BSMS Rapid X" 
                className="h-24 w-auto object-contain"
              />
            </div>
            <CardTitle className="text-2xl font-rajdhani font-bold text-white tracking-wide flex items-center justify-center gap-2">
              <Shield className="w-6 h-6 text-blue-400" />
              RAPID X
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Security Operations Platform
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-sm bg-red-500/10 border border-red-500/30 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400" />
                  <span className="text-sm text-red-400">{error}</span>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-300 font-inter">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@rapidx.security"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-[#09090b] border-[#3f3f46] text-white placeholder:text-zinc-600 font-mono text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-300 font-inter">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-[#09090b] border-[#3f3f46] text-white placeholder:text-zinc-600 font-mono text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                    data-testid="login-password-input"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-rajdhani font-semibold tracking-wide rounded-sm shadow-lg shadow-blue-500/30 transition-all duration-200"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'AUTHENTICATING...' : 'ACCESS SYSTEM'}
              </Button>
              
              <div className="text-center">
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-zinc-400 hover:text-blue-400 transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </Link>
              </div>
            </form>
            
            <div className="mt-6 pt-6 border-t border-[#3f3f46]">
              <p className="text-xs text-zinc-500 text-center font-mono">
                Rapid X v1.0.0 | Authorized Access Only
              </p>
              <p className="text-xs text-zinc-600 text-center mt-1">
                {(process.env.REACT_APP_APP_URL || window.location.origin).replace('https://', '').replace('http://', '')}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Right side - Hero image */}
      <div 
        className="hidden lg:block lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: `url(${HERO_IMAGE_URL})`
        }}
      >
        {/* Gradient overlay from left */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] via-[#09090b]/60 to-transparent" />
        
        {/* Bottom content overlay */}
        <div className="absolute bottom-8 left-8 right-8">
          <div className="p-5 bg-black/60 backdrop-blur-md rounded-lg border border-blue-500/30">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
              <span className="text-xs text-blue-400 font-mono uppercase tracking-wider">System Active</span>
            </div>
            <p className="text-base text-zinc-200 font-inter leading-relaxed">
              "Elite security operations powered by cutting-edge technology. Protecting assets, securing communities."
            </p>
            <p className="text-xs text-zinc-500 mt-3 font-mono">— Rapid X Tech Pty Ltd</p>
          </div>
        </div>
        
        {/* Top-right badge */}
        <div className="absolute top-8 right-8">
          <div className="px-4 py-2 bg-blue-500/20 backdrop-blur-sm rounded border border-blue-500/40">
            <p className="text-sm text-blue-400 font-rajdhani font-bold tracking-wide">RAPID X</p>
            <p className="text-xs text-zinc-400">Security Operations</p>
          </div>
        </div>
      </div>
    </div>
  );
}
